import { Component, OnInit, ViewChild, inject, signal } from '@angular/core'
import { EnqueteService } from '../../services/enquete.service'
import { ActivatedRoute } from '@angular/router'
import { EnqueteDto } from '../../models/enquete-dto'
import { ToastrService } from 'ngx-toastr'
import { NgbDateStruct, NgbModal, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap'
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { formatDateStructForBack, lancementIsValid, scrollToTop, shouldDisplayInvalid } from '../../utils/utils'
import { NgClass } from '@angular/common'
import { FormatDateFromBack } from '../../pipes/format-date-from-back.pipe'

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgbInputDatepicker, NgClass, FormatDateFromBack],
    standalone: true,
})
export class HomeComponent implements OnInit {
    enqueteService = inject(EnqueteService)
    private readonly route = inject(ActivatedRoute)
    private readonly formBuilder = inject(FormBuilder)
    toastr = inject(ToastrService)
    modalService = inject(NgbModal)

    validate = signal<string | null>(null)
    enqueteId = signal<string | null>(null)
    statutLancement: string
    delayedLaunch = signal<boolean | null>(null)
    fetchingEnquete = signal(false)
    enquete = signal<EnqueteDto | null>(null)
    sendingValidation = signal(false)
    @ViewChild('refusModal') refusModal: any
    validateEnqueteForm: FormGroup
    protected readonly shouldDisplayInvalid = shouldDisplayInvalid

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            this.fetchingEnquete.set(true)

            const uriEncodedValidate = params['validate']
            const encodedValidate = uriEncodedValidate ? decodeURIComponent(uriEncodedValidate) : null
            this.validate.set(encodedValidate ? atob(encodedValidate) : null)

            const val = this.validate()
            if (val) {
                const validateQuery = new URLSearchParams(val)
                const eqId = validateQuery.get('enqueteId')
                this.enqueteId.set(eqId)
                this.statutLancement = validateQuery.get('statutLancement')

                const delayedLaunchQueryParam = validateQuery.get('delayedLaunch')
                this.delayedLaunch.set(delayedLaunchQueryParam?.length > 0 ? delayedLaunchQueryParam === 'true' : null)

                const gestionSavQueryParam = validateQuery.get('gestionSav')
                const gestionSav = gestionSavQueryParam?.length > 0 ? gestionSavQueryParam === 'true' : null

                if (eqId) {
                    this.enqueteService.getEnquete(eqId).subscribe((value) => {
                        this.enquete.set(value)
                        if (this.statutLancement === 'REFUSLG' && this.enqueteCanBeValidated()) {
                            this.openValidationModal()
                        }

                        const currentEnquete = this.enquete()
                        if (currentEnquete?.typeEnquete === 'GPA' && this.delayedLaunch()) {
                            this.validateEnqueteForm = this.formBuilder.group({
                                gestionSav: [gestionSav, [Validators.required]],
                                delayedLaunch: [this.getTodayWoTime(), []],
                            })
                        } else if (currentEnquete?.typeEnquete === 'GPA') {
                            this.validateEnqueteForm = this.formBuilder.group({
                                gestionSav: [gestionSav, [Validators.required]],
                            })
                        } else if (this.delayedLaunch()) {
                            this.validateEnqueteForm = this.formBuilder.group({
                                delayedLaunch: [this.getTodayWoTime(), []],
                            })
                        } else {
                            this.validateEnqueteForm = this.formBuilder.group({})
                        }
                        this.fetchingEnquete.set(false)
                    })
                } else {
                    this.fetchingEnquete.set(false)
                }
            } else {
                this.fetchingEnquete.set(false)
            }
        })
    }

    validateEnquete(isValidation: boolean) {
        if (this.validateEnqueteForm.valid || !isValidation) {
            this.sendingValidation.set(true)
            this.enqueteService
                .validateEnquete({
                    enqueteId: this.enqueteId(),
                    statutLancement: isValidation ? 'VALIDE' : 'REFUSLG',
                    gestionSav: isValidation ? this.validateEnqueteForm.get('gestionSav')?.value : null,
                    launchDate: this.delayedLaunch() ? formatDateStructForBack(this.validateEnqueteForm.get('delayedLaunch')?.value) : null,
                })
                .subscribe({
                    next: (value) => {
                        scrollToTop()
                        if (value) {
                            this.enquete.set(value)
                            this.toastr.success('Vous pouvez fermer la fenêtre', 'Enquête ' + (isValidation ? 'validée' : 'refusée'))
                        }
                        this.fetchingEnquete.set(false)
                        this.sendingValidation.set(false)
                        this.dismissValidationModal()
                    },
                    error: () => {
                        scrollToTop()
                        this.fetchingEnquete.set(false)
                        this.sendingValidation.set(false)
                        this.dismissValidationModal()
                    },
                })
        }
    }

    openValidationModal() {
        this.modalService.open(this.refusModal, { fullscreen: true })
    }

    dismissValidationModal() {
        this.modalService.dismissAll()
    }

    getStatutEnqueteLabel(): string {
        const lancement = this.enquete()?.sat?.lancement
        return !lancement ? 'En attente de validation' : lancement
    }

    getTypeEnqueteLabel(): string {
        const typeEnquete = this.enquete()?.typeEnquete
        if (typeEnquete == 'ACHAUD') {
            return 'À Chaud'
        }
        if (typeEnquete == 'BILAN') {
            return 'Bilan'
        }
        if (typeEnquete == 'GPA') {
            return 'GPA'
        }
        if (typeEnquete == 'PERDU') {
            return 'Perdue'
        }
        return '-'
    }

    enqueteCanBeValidated(): boolean {
        const currentEnquete = this.enquete()
        return (!currentEnquete?.sat?.lancement || currentEnquete?.sat?.lancement === 'A valider') && (!!currentEnquete?.intervenantMOA || !!currentEnquete?.intervenantMOE || !!currentEnquete?.intervenantAUTRE)
    }

    getTodayWoTime(): NgbDateStruct {
        const todayWithTime = new Date()

        return {
            year: todayWithTime.getFullYear(),
            month: todayWithTime.getMonth() + 1,
            day: todayWithTime.getDate(),
        }
    }

    getMaxDate(): NgbDateStruct {
        const today = this.getTodayWoTime()
        const aYearFromNow = new Date(today.year + 1, today.month, today.day)

        return {
            year: aYearFromNow.getFullYear(),
            month: aYearFromNow.getMonth(),
            day: aYearFromNow.getDate(),
        }
    }

    protected readonly lancementIsValid = lancementIsValid
}
